import { ReactNode } from 'react';
import Link from 'next/link';

type ButtonProps = {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'light' | 'elevated';
  size?: 'sm' | 'md' | 'lg';
  href?: string;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
};

const Button = ({
  variant = 'primary',
  size = 'md',
  href,
  children,
  className = '',
  ...props
}: ButtonProps) => {
  const baseStyles = 'inline-flex items-center justify-center font-semibold rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500';
  
  const variants = {
    primary: 'bg-gradient-to-r from-soft-gold to-amber-200 text-warm-brown border border-soft-gold/70 shadow-lg hover:shadow-xl hover:-translate-y-0.5 hover:from-soft-gold/90 hover:to-amber-200/90',
    secondary: 'bg-white/10 hover:bg-white/15 text-white border border-white/25 backdrop-blur-sm',
    outline: 'border border-soft-gold/80 text-soft-gold hover:bg-soft-gold hover:text-warm-brown hover:border-soft-gold',
    ghost: 'hover:bg-gray-100 text-gray-700',
    light: 'bg-white text-warm-brown border border-white/80 shadow-sm hover:bg-white/95 hover:border-white',
    elevated: 'bg-soft-gold/10 text-soft-gold border border-soft-gold/60 shadow-md hover:bg-soft-gold hover:text-white hover:border-soft-gold'
  };

  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };

  const buttonClasses = `${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`;

  if (href) {
    return (
      <Link href={href} className={buttonClasses} {...props}>
        {children}
      </Link>
    );
  }

  return (
    <button className={buttonClasses} {...props}>
      {children}
    </button>
  );
};

export { Button };
export default Button;
